import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { getImpersonatedOrgId } from "../../lib/api";
import { isRestrictablePath } from "../../lib/restrictablePages";

/**
 * Match the current pathname against one of the page paths from
 * ``user.allowed_pages``. We strip trailing slashes and treat the stored
 * path as a prefix so "/donors" also allows "/donors/123" and
 * "/donors/new".
 */
function matchesAllowedPage(pathname, allowedPages) {
  if (!Array.isArray(allowedPages)) return true; // no restriction
  const path = pathname.replace(/\/+$/, "") || "/";
  return allowedPages.some((p) => {
    if (!p) return false;
    const clean = p.replace(/\/+$/, "") || "/";
    if (path === clean) return true;
    return path.startsWith(clean + "/");
  });
}

export default function ProtectedRoute({ allowedRoles, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        data-testid="protected-loader"
        className="min-h-screen flex items-center justify-center bg-[#FAFAF7]"
      >
        <Loader2 className="h-8 w-8 animate-spin text-stone-700" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Owner can act as admin of any organization. When impersonating, grant
  // access to admin/manager-only routes.
  const impersonating = user.role === "owner" && !!getImpersonatedOrgId();
  const effectiveRoles = impersonating ? [user.role, "admin", "manager"] : [user.role];

  if (
    allowedRoles &&
    !allowedRoles.some((r) => effectiveRoles.includes(r))
  ) {
    const target = user.role === "owner" ? "/owner/dashboard" : "/dashboard";
    return <Navigate to={target} replace />;
  }

  // Page-level restriction : ne s'applique QU'aux pages de contenu
  // restreignables. Les routes essentielles / de compte (Sécurité, Mon
  // abonnement, connexion Stripe, espace owner…) ne sont jamais restreignables
  // → toujours joignables (gardées par le rôle seul). Sans ça, un membre
  // restreint cliquant une de ces pages était rebondi vers sa 1re page
  // autorisée (bug "Sécurité / Mon abonnement / Stripe → Post-Chabat").
  if (
    user.allowed_pages &&
    isRestrictablePath(location.pathname) &&
    !matchesAllowedPage(location.pathname, user.allowed_pages)
  ) {
    // Send the user to the first page they're allowed on, or a safe default.
    const fallback =
      user.allowed_pages.find((p) => p && p !== location.pathname) ||
      (user.role === "owner" ? "/owner/dashboard" : "/dashboard");
    return <Navigate to={fallback} replace />;
  }

  return children;
}

export { matchesAllowedPage };
